'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useResetPasswordMutation } from '@/features/api/authApi';

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please confirm your password correctly.',
        variant: 'destructive',
      });
      return;
    }

    console.log('Submitting reset password payload:', { token, password });

    try {
      const res = await resetPassword({ token, password }).unwrap();
      console.log('Reset password response:', res);

      // Show popup modal
      setModalMessage(res?.message || 'Password reset successfully!');
      setIsModalOpen(true);

      setPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Reset password error:', error);
      toast({
        title: 'Error',
        description: error?.data?.message || 'Password reset failed.',
        variant: 'destructive',
      });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    navigate('/login');
  };

  return (
    <div className="flex min-h-[65vh] items-center justify-center bg-background p-4 relative">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="inline-flex justify-center items-center gap-2 mb-3">
            {/* <Leaf className="h-10 w-10 text-primary" /> */}
          </div>
          <CardTitle className="text-2xl font-bold">Reset Password</CardTitle>
        </CardHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <CardContent className="p-4 space-y-4">
            {/* New Password Field */}
            <div className="relative">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                className="h-12 text-base pr-10"
              />
              <button
                type="button"
                className="absolute right-3 top-9 text-gray-500 hover:text-black"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Confirm Password Field */}
            <div className="relative">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
                className="h-12 text-base pr-10"
              />
              <button
                type="button"
                className="absolute right-3 top-9 text-gray-500 hover:text-black"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-3 p-4">
            <Button type="submit" className="w-full h-12 text-base" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
              Reset Password
            </Button>
            <div className="text-center text-sm">
              <Link to="/login" className="underline">
                Back to Login
              </Link>
            </div>
          </CardFooter>
        </form>
      </Card>

      {/* Success Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-lg text-center">
            <CheckCircle2 className="mx-auto mb-4 h-10 w-10 text-green-500" />
            <h2 className="text-xl font-bold mb-2">Password Updated Successfully!</h2>
            <p className="mb-4">{modalMessage}</p>
            <Button onClick={handleCloseModal} className="w-full">
              Go to Login
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
